import React, { useCallback, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Table from '~/components/common/Table';
import { TColumn } from '~/components/common/Table/Table';
import { UserStatus, IUser } from '~/models/user';
import themes, { styled } from '~/themes';
import { getNewSort } from '~/utils/common';
import {
  formatPhoneNumber,
  updateLocationSearchParams,
} from '~/utils/formatter';
import {
  addToQueryString,
  fromQueryString,
  omitFromQueryString,
} from '~/utils/queryString';
import { CustomerSupportTableLabels, ICustomerSupport } from '~/models/driver';
import { usePaymentInfo } from '~/providers/PaymentProvider';
import { Stack } from '@mui/system';
import { DriverAvatar } from '~/components/shared/AssignTask/DriverAvatar';
import { Box, Typography } from '@mui/material';
import { ToolTipListText } from '~/components/common/Tooltip/TooltipListText';
import { DriverStatus } from '~/pages/LiveTracking/types';
import { IDriver } from '../types';
import { DRIVER_STATUS } from '../constants';
import ShiftSchedule from './ShiftSchedule';

const EmptyStateData = [
  {
    first_name: 'John',
    last_name: 'Doe',
    display_name: 'John Doe',
    phone: '+11010101010',
    role: 'driver',
    default: false,
    status: 'active',
    driver_status: 'online',
    customer_support: ICustomerSupport.ContactDriverPhone,
    dob: null,
    teams: [
      {
        name: 'Day Shift',
        description: 'Day Shift',
        hub_id: null,
        id: '65113e8f1693da22d530739a',
      },
    ],
    id: '6549b858f79b38b8e8f462a1',
  },
];

export const StyledDriverStatus = styled('span', {
  shouldForwardProp: (prop) => prop !== 'status',
})<{ status: UserStatus | DriverStatus }>(({ status, theme }) => ({
  padding: '4px 8px',
  borderRadius: '4px',
  ...(status === UserStatus.Pending && {
    color: theme.color.orange,
    backgroundColor: 'rgba(254, 116, 0, 0.10)',
  }),
  ...(status === UserStatus.Online && {
    color: theme.color.green500,
    backgroundColor: 'rgba(27, 196, 125, 0.10)',
  }),
  ...(status === UserStatus.Idle && {
    color: theme.color.orange300,
    backgroundColor: 'rgba(243, 186, 80, 0.10)',
  }),
  ...(status === UserStatus.Offline && {
    color: 'rgba(51, 54, 61, 0.40)',
    backgroundColor: 'rgba(51, 54, 61, 0.05)',
  }),
}));

interface IDriverTableProps {
  data: IDriver[];
  isLoading: boolean;
  isEmptyState?: boolean;
  renderEmpty?: React.ReactNode;
}

export const DriverTable: React.FC<IDriverTableProps> = ({
  data,
  isLoading,
  isEmptyState,
  renderEmpty,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isStarterOrStandardPlan } = usePaymentInfo();
  const sourceType =
    useParams<{ sourceType: string; source: string }>()?.sourceType ||
    UserStatus.Active;
  const { id: driverId, sort, sort_by } = fromQueryString(location.search);

  const handleSort = useCallback(
    (sortId: string, nextArrange: number) => {
      const sortParams = getNewSort(sortId, nextArrange);
      navigate({
        pathname: location.pathname,
        search: `?${updateLocationSearchParams(location, sortParams)}`,
      });
    },
    [location],
  );

  const renderShiftSchedule = useCallback(
    ({ delivery }: IUser) => {
      if (!delivery || !delivery.enable_delivery) {
        return (
          <Typography
            sx={{
              color: 'rgba(51, 54, 61, 0.40)',
            }}
          >
            Shift schedule is not enabled
          </Typography>
        );
      }
      return <ShiftSchedule data={delivery?.delivery_hours} />;
    },
    [sourceType, isEmptyState],
  );

  const renderStatus = useCallback(
    ({ status, display_name, driver_status }: IDriver) => (
      <StyledDriverStatus
        role='presentation'
        data-testid={`${display_name}-status`}
        status={status === UserStatus.Active ? driver_status : status}
      >
        {DRIVER_STATUS[status === UserStatus.Active ? driver_status : status]}
      </StyledDriverStatus>
    ),
    [sourceType],
  );

  const renderAvatar = (item: IUser) => (
    <Stack direction='row' alignItems='center'>
      <DriverAvatar size={40} avatar={item.avatar} name={item.display_name} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          ml: 1,
        }}
      >
        <Typography data-testid='display-name-driver'>
          {item?.display_name}
        </Typography>
        <Typography data-testid='display-phone-driver'>
          {isEmptyState ? '(+1) 101-010-1010' : formatPhoneNumber(item?.phone)}
        </Typography>
      </Box>
    </Stack>
  );

  const columns: TColumn[] = useMemo(
    () => [
      {
        id: 'display_name',
        title: 'Driver',
        cellStyle: { minWidth: 190 },
        sortable: true,
        renderComponent: renderAvatar,
      },
      {
        id: 'status',
        title: 'Status',
        cellStyle: { minWidth: 130 },
        sortable: true,
        renderComponent: renderStatus,
      },
      {
        id: 'team_names',
        title: 'Team',
        cellStyle: { minWidth: 220 },
        sortable: false,
        renderComponent: ({ teams }: IUser) => {
          if (isStarterOrStandardPlan && !isEmptyState) return '-';
          return (
            <ToolTipListText list={teams.map((team) => team.name)} max={3} />
          );
        },
      },
      {
        id: 'customer_support',
        title: 'Customer support option',
        cellStyle: { minWidth: 220 },
        sortable: true,
        renderComponent: (item) =>
          isEmptyState
            ? 'Customers can only contact the driver’s phone number'
            : CustomerSupportTableLabels[item.customer_support] ||
              CustomerSupportTableLabels[ICustomerSupport.ContactDriverPhone],
      },
      {
        id: 'delivery',
        title: 'Shift schedule',
        cellStyle: { minWidth: 400 },
        renderComponent: renderShiftSchedule,
      },
    ],
    [sourceType, isStarterOrStandardPlan, isEmptyState],
  );

  const clickableRowId = useMemo(
    () => driverId && data.findIndex((x) => x.id === driverId),
    [data, driverId],
  );

  const onRowClick = useCallback(
    (item: any) => {
      let search;
      if (item.id === driverId) {
        search = omitFromQueryString(location.search, ['id']);
      } else {
        search = addToQueryString(location.search, {
          id: item.id,
        });
      }
      navigate({
        pathname: location.pathname,
        search,
      });
    },
    [location],
  );

  return (
    <Table
      noHoverInteraction
      columns={columns}
      dataProps={isEmptyState ? EmptyStateData : data || []}
      loading={isLoading}
      sortBy={Number(sort) ? { [sort_by as string]: Number(sort) } : {}}
      onSort={handleSort}
      clickableRowId={clickableRowId}
      onRowClick={isEmptyState ? undefined : onRowClick}
      customEmptyState={isEmptyState ? renderEmpty : undefined}
      sxNoData={{ padding: '35px !important' }}
      containerSxProps={{
        ...(isEmptyState
          ? {
              '& td': {
                'filter': 'grayscale(1)',
                'opacity': 0.25,
                '&:last-child': {
                  pointerEvents: 'none',
                },
                '& span': {
                  color: 'black !important',
                },
              },
            }
          : {}),
      }}
      cellSxProps={{
        color: themes.color.violet500,
        py: '8px !important',
      }}
      headerCellSxProps={{
        color: themes.color.gray400,
        backgroundColor: themes.color.gray200,
        fontSize: 12,
        paddingTop: '4px !important',
        paddingBottom: '4px !important',
        border: 'none',
      }}
    />
  );
};
